import { t } from "i18next";
import { useSearchParams } from "react-router";
import { UpdateHospitalityMenuMetaForm } from "./updateHospitalityMenu/UpdateHospitalityMenuMetaForm";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useQuery } from "@tanstack/react-query";
import { getHospitalityMenuDetails } from "@/sideEffects/api";
import { HospitalityMenuCategories } from "./hospitalityMenuCategories/HospitalityMenuCategories";
import { AddNewHospitalityMenuCategoryDialog } from "./hospitalityMenuCategories/AddNewHospitalityMenuCategoryDialog";
import { SyncHospitalityMenuButton } from "../SyncHospitalityMenuButton";
import { DeleteHospitalityMenuButton } from "../DeleteHospitalityMenuButton";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Separator } from "@/components/ui/separator";

export const HospitalityMenuDetailPage = () => {
  const [queryParams] = useSearchParams();
  const hospitalityMenuId = queryParams.get("Id");

  const { data: hospitalityMenuDetails, isLoading } = useQuery({
    queryKey: ["getHospitalityMenu", hospitalityMenuId],
    queryFn: () => getHospitalityMenuDetails(Number(hospitalityMenuId)),
    enabled: !!hospitalityMenuId,
  });

  const hospitalityMenuDetailsData = hospitalityMenuDetails?.data;

  useBreadcrumbs([
    {
      name: t("entities.hospitalityMenu"),
      path: ROUTES.HOSPITALITY_MENU,
    },
    {
      name: hospitalityMenuDetailsData?.name || "...",
      path: ROUTES.HOSPITALITY_MENU_DETAIL,
    },
  ]);

  const isMobile = useIsMobile();

  if (!hospitalityMenuDetailsData || isLoading) {
    return <LoadingSmall />;
  }

  const header = (
    <div className="flex justify-end items-center gap-2">
      <SyncHospitalityMenuButton hospitalityMenu={hospitalityMenuDetailsData} />
      <DeleteHospitalityMenuButton hospitalityMenu={hospitalityMenuDetailsData} />
    </div>
  );

  const form = (
    <>
      <div className="mt-2">
        <UpdateHospitalityMenuMetaForm hospitalityMenu={hospitalityMenuDetailsData} />
      </div>
    </>
  );

  const categories = (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold mb-2 mt-2">{t("hospitalityMenu.categories")}</h2>
        <AddNewHospitalityMenuCategoryDialog hospitalityMenu={hospitalityMenuDetailsData} />
      </div>
      <HospitalityMenuCategories hospitalityMenu={hospitalityMenuDetailsData} />
    </>
  );

  if (isMobile) {
    return (
      <div>
        {header}
        {form}
        <Separator className="my-4" />
        {categories}
      </div>
    );
  }

  return (
    <div>
      {header}
      <ResizablePanelGroup
        className="flex gap-2 border-t mt-2 min-h-[500px]"
        direction={"horizontal"}
        autoSaveId="hospitality-menu-detail"
      >
        <ResizablePanel minSize={20} defaultSize={50}>
          {form}
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel minSize={40} defaultSize={50}>
          {categories}
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};
